import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import $ from 'jquery';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as action from '../Action/Tags/Tags_Action';
import Model from './Model/Index';

/**
 * Priority Genres — the curated, ordered subset of the Genres (tags) list.
 *
 * Backed by three calls on the tags resource:
 *   POST /tags/get              { filter: 'priority' }        → the ordered list
 *   POST /tags/update           { id, name, priority: 0|1 }   → add / remove
 *   POST /tags/reorder-priority { ordered_ids: [...] }        → persist the order
 *
 * The backend owns rank assignment: flipping `priority` to 1 appends the genre at
 * the end of the list, and flipping it to 0 re-indexes everything after it. This
 * screen only ever sends a complete, contiguous `ordered_ids` array.
 */

// `limit` is the page-size key /tags/get honours (same one the All Genres table
// paginates with). One page is enough for a curated list; `truncated` below is
// the guard in case it ever isn't.
const FETCH_ALL = { page: 1, limit: 1000 };

/**
 * Hard cap on the priority list. The app surfaces these genres first and only has
 * room for six, so both entry points — this screen's picker and the star toggle
 * on the All Genres table — refuse to go past it.
 *
 * The backend does not enforce the ceiling, so a list saved before this cap
 * existed can still arrive over-full; that is reported and has to be trimmed by
 * hand rather than silently truncated.
 */
export const MAX_PRIORITY_GENRES = 6;

const isPriority = (genre) => genre?.priority === true || Number(genre?.priority) > 0;

/** Pulls the row array out of a /tags/get response, dropping duplicate ids. */
const extractRows = (res) => {
    const rd = res?.responseDetails || res?.data || res || {};
    const raw = rd.rows || rd.data || (Array.isArray(rd) ? rd : []);
    const seen = new Map();
    raw.forEach((row) => {
        if (row && row.id != null && !seen.has(row.id)) seen.set(row.id, row);
    });
    return Array.from(seen.values());
};

/** Reported total for a /tags/get response, or null when it isn't given. */
const extractTotal = (res) => {
    const rd = res?.responseDetails || res?.data || res || {};
    const total = rd.total ?? rd.totalRecords;
    return typeof total === 'number' ? total : null;
};

/** Ascending priority_rank; unranked rows fall to the bottom, then by name. */
const byPriorityRank = (a, b) => {
    const ar = a?.priority_rank;
    const br = b?.priority_rank;
    if (ar == null && br == null) {
        return String(a?.name || '').localeCompare(String(b?.name || ''));
    }
    if (ar == null) return 1;
    if (br == null) return -1;
    return Number(ar) - Number(br);
};

const sameOrder = (a, b) =>
    a.length === b.length && a.every((item, i) => item.id === b[i].id);

const SortableGenreRow = ({
    item,
    index,
    total,
    rankValue,
    onRankChange,
    onRankCommit,
    onRemove,
    allowEdit,
    busy,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: item.id, disabled: !allowEdit || busy });

    return (
        <div
            ref={setNodeRef}
            className={`priority-row${isDragging ? ' is-dragging' : ''}`}
            style={{ transform: CSS.Transform.toString(transform), transition }}
        >
            <button
                type="button"
                className="priority-handle"
                title={allowEdit ? 'Drag to reorder' : 'Reordering needs edit permission'}
                disabled={!allowEdit || busy}
                {...attributes}
                {...listeners}
            >
                <i className="fas fa-grip-vertical"></i>
            </button>

            <span className="priority-rank-badge">#{index + 1}</span>

            <span className="priority-name" title={item.name}>{item.name}</span>

            {allowEdit && (
                <div className="priority-rank-set">
                    <label htmlFor={`priority-rank-${item.id}`}>Rank</label>
                    <input
                        id={`priority-rank-${item.id}`}
                        type="number"
                        min="1"
                        max={total}
                        value={rankValue}
                        disabled={busy}
                        onChange={(e) => onRankChange(item.id, e.target.value)}
                        onBlur={() => onRankCommit(item, index)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                onRankCommit(item, index);
                            }
                        }}
                    />
                </div>
            )}

            {allowEdit && (
                <button
                    type="button"
                    className="priority-remove"
                    title="Remove from priority"
                    disabled={busy}
                    onClick={() => onRemove(item)}
                >
                    <i className="fas fa-times"></i>
                </button>
            )}
        </div>
    );
};

const PriorityGenres = ({ allowEdit, onDirtyChange, onCountChange }) => {
    const dispatch = useDispatch();

    const [items, setItems] = useState([]);
    const [originalItems, setOriginalItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState(false);
    const [truncated, setTruncated] = useState(false);
    const [rankDrafts, setRankDrafts] = useState({});

    const [showPicker, setShowPicker] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [candidatesLoading, setCandidatesLoading] = useState(false);
    const [pickerSearch, setPickerSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [adding, setAdding] = useState(false);

    const dirty = useMemo(() => !sameOrder(items, originalItems), [items, originalItems]);

    const remainingSlots = Math.max(MAX_PRIORITY_GENRES - items.length, 0);
    const atCapacity = items.length >= MAX_PRIORITY_GENRES;
    // Only reachable from data saved before the cap existed, or written by
    // something other than this screen.
    const overCapacity = items.length > MAX_PRIORITY_GENRES;

    const dirtyChangeRef = useRef(onDirtyChange);
    dirtyChangeRef.current = onDirtyChange;
    const countChangeRef = useRef(onCountChange);
    countChangeRef.current = onCountChange;

    useEffect(() => {
        dirtyChangeRef.current?.(dirty);
        return () => dirtyChangeRef.current?.(false);
    }, [dirty]);

    useEffect(() => {
        countChangeRef.current?.(items.length);
    }, [items.length]);

    const fetchPriority = useCallback((showSpinner = true) => {
        if (showSpinner) setLoading(true);
        return dispatch(action.getTags({ ...FETCH_ALL, filter: 'priority' }))
            .then((res) => {
                const rows = extractRows(res).filter(isPriority).sort(byPriorityRank);
                const total = extractTotal(res);
                setItems(rows);
                setOriginalItems(rows);
                setRankDrafts({});
                // A reorder must send every priority id; a partial page would
                // leave the rest with stale ranks, so refuse to save instead.
                setTruncated(total !== null && total > rows.length);
            })
            .catch((err) => {
                toast.error(err?.responseMessage || err?.message || 'Failed to load priority genres.');
            })
            .finally(() => setLoading(false));
    }, [dispatch]);

    useEffect(() => { fetchPriority(); }, [fetchPriority]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setItems((prev) => {
            const from = prev.findIndex((i) => i.id === active.id);
            const to = prev.findIndex((i) => i.id === over.id);
            if (from === -1 || to === -1) return prev;
            return arrayMove(prev, from, to);
        });
    };

    const clearRankDraft = (id) => setRankDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
    });

    const onRankChange = (id, value) => setRankDrafts((prev) => ({ ...prev, [id]: value }));

    const onRankCommit = (item, index) => {
        const raw = rankDrafts[item.id];
        clearRankDraft(item.id);
        if (raw === undefined || String(raw).trim() === '') return;
        const parsed = parseInt(raw, 10);
        if (Number.isNaN(parsed)) return;
        const target = Math.min(Math.max(parsed, 1), items.length) - 1;
        if (target === index) return;
        setItems((prev) => arrayMove(prev, index, target));
    };

    const resetOrder = () => {
        setItems(originalItems);
        setRankDrafts({});
    };

    const saveOrder = () => {
        setSaving(true);
        dispatch(action.reorderTagsPriority({ ordered_ids: items.map((i) => i.id) }))
            .then((res) => {
                toast.success(res?.message || res?.responseMessage || 'Priority order saved.');
                return fetchPriority(false);
            })
            .catch((err) => {
                toast.error(err?.responseMessage || err?.message || 'Failed to save priority order.');
            })
            .finally(() => setSaving(false));
    };

    /** Add/remove refetches the list, which would throw away a pending reorder. */
    const blockedByUnsavedOrder = () => {
        if (!dirty) return false;
        Swal.fire({
            title: 'Unsaved order',
            html: 'Save or reset the current order before changing which genres are in the priority list.',
            icon: 'info',
            confirmButtonText: 'Got it',
            confirmButtonColor: 'var(--first-color)',
            customClass: { confirmButton: 'btn' },
        });
        return true;
    };

    const removeFromPriority = (item) => {
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return;
        }
        if (blockedByUnsavedOrder()) return;

        Swal.fire({
            title: `Remove "${item.name}"?`,
            text: 'It stays a genre — only its priority placement is removed. Ranks below it shift up.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--first-color)',
            cancelButtonColor: 'var(--error-color)',
            confirmButtonText: 'Yes, remove it',
            customClass: { confirmButton: 'btn', cancelButton: 'btn' },
        }).then((result) => {
            if (!result.isConfirmed) return;
            setBusy(true);
            dispatch(action.updateTags({ id: item.id, name: item.name, priority: 0 }))
                .then(() => {
                    toast.success(`"${item.name}" removed from priority.`);
                    return fetchPriority(false);
                })
                .catch((err) => {
                    toast.error(err?.responseMessage || err?.message || 'Failed to remove from priority.');
                })
                .finally(() => setBusy(false));
        });
    };

    const openPicker = () => {
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return;
        }
        if (blockedByUnsavedOrder()) return;
        if (atCapacity) {
            toast.error(
                `The priority list is full at ${MAX_PRIORITY_GENRES} genres. Remove one before adding another.`,
            );
            return;
        }

        setShowPicker(true);
        setPickerSearch('');
        setSelectedIds([]);
        $('body').addClass('no_scroll');
        $('.overlay').addClass('qv_active');

        setCandidatesLoading(true);
        dispatch(action.getTags(FETCH_ALL))
            .then((res) => {
                const rows = extractRows(res)
                    .filter((g) => !isPriority(g))
                    .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
                setCandidates(rows);
            })
            .catch((err) => {
                toast.error(err?.responseMessage || err?.message || 'Failed to load genres.');
                setCandidates([]);
            })
            .finally(() => setCandidatesLoading(false));
    };

    const closePicker = () => {
        setShowPicker(false);
        setSelectedIds([]);
        setPickerSearch('');
        $('body').removeClass('no_scroll');
        $('.overlay').removeClass('qv_active');
    };

    const toggleSelected = (id) => setSelectedIds((prev) => {
        if (prev.includes(id)) return prev.filter((x) => x !== id);
        if (prev.length >= remainingSlots) {
            toast.error(
                `Only ${remainingSlots} slot${remainingSlots === 1 ? '' : 's'} left in the ${MAX_PRIORITY_GENRES}-genre priority list.`,
            );
            return prev;
        }
        return [...prev, id];
    });

    const filteredCandidates = useMemo(() => {
        const term = pickerSearch.trim().toLowerCase();
        if (!term) return candidates;
        return candidates.filter((g) => String(g?.name || '').toLowerCase().includes(term));
    }, [candidates, pickerSearch]);

    const addSelected = async () => {
        // Walk `selectedIds`, not `candidates` — the former is in the order the
        // admin ticked the boxes, and the backend appends in the order it is sent,
        // so that pick order becomes the rank order.
        const byId = new Map(candidates.map((g) => [g.id, g]));
        const chosen = selectedIds.map((id) => byId.get(id)).filter(Boolean);
        if (chosen.length === 0) {
            toast.error('Select at least one genre.');
            return;
        }
        if (chosen.length > remainingSlots) {
            toast.error(
                `Only ${remainingSlots} slot${remainingSlots === 1 ? '' : 's'} left in the ${MAX_PRIORITY_GENRES}-genre priority list.`,
            );
            return;
        }

        setAdding(true);
        const failed = [];
        // Sequential: the backend appends each one to the end of the rank list, so
        // the order the admin picked them in becomes the order they land in.
        for (const genre of chosen) {
            try {
                await dispatch(action.updateTags({ id: genre.id, name: genre.name, priority: 1 }));
            } catch (err) {
                failed.push(genre.name);
            }
        }
        setAdding(false);

        const added = chosen.length - failed.length;
        if (added > 0) {
            toast.success(`${added} genre${added > 1 ? 's' : ''} added to priority.`);
        }
        if (failed.length > 0) {
            toast.error(`Could not add: ${failed.join(', ')}`);
        }

        closePicker();
        fetchPriority(false);
    };

    return (
        <>
            <div className="priority-genres">
                <div className="priority-toolbar">
                    <div className="priority-intro">
                        <h5>
                            Priority Genres
                            <span className={`priority-slot-count${atCapacity ? ' is-full' : ''}`}>
                                {items.length} / {MAX_PRIORITY_GENRES}
                            </span>
                        </h5>
                        <p>
                            These genres surface first in the app, in the order below. Drag the
                            <i className="fas fa-grip-vertical mx-1"></i>
                            handle or type a rank, then save. Up to {MAX_PRIORITY_GENRES} genres
                            can be promoted.
                        </p>
                    </div>

                    {allowEdit && (
                        <div className="priority-actions">
                            {dirty && (
                                <button
                                    type="button"
                                    className="btn priority-btn-ghost"
                                    onClick={resetOrder}
                                    disabled={saving || busy}
                                >
                                    <i className="fas fa-undo me-2"></i>Reset
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn priority-btn-ghost"
                                onClick={openPicker}
                                disabled={saving || busy || atCapacity}
                                title={atCapacity
                                    ? `The priority list is full at ${MAX_PRIORITY_GENRES} genres`
                                    : undefined}
                            >
                                <i className="fas fa-plus me-2"></i>Add Genre
                            </button>
                            <button
                                type="button"
                                className="btn upgrade-btn"
                                onClick={saveOrder}
                                disabled={!dirty || saving || busy || truncated}
                            >
                                {saving
                                    ? <><i className="fas fa-spinner fa-spin me-2"></i>Saving...</>
                                    : <><i className="fas fa-save me-2"></i>Save Order</>}
                            </button>
                        </div>
                    )}
                </div>

                {truncated && (
                    <div className="priority-dirty-note priority-blocked-note">
                        <i className="fas fa-triangle-exclamation me-2"></i>
                        Only part of the priority list loaded, so reordering is disabled.
                        Reload the page and try again.
                    </div>
                )}

                {overCapacity && (
                    <div className="priority-dirty-note priority-blocked-note">
                        <i className="fas fa-triangle-exclamation me-2"></i>
                        This list holds {items.length} genres but the limit is {MAX_PRIORITY_GENRES}.
                        Remove {items.length - MAX_PRIORITY_GENRES} to bring it back within the cap.
                    </div>
                )}

                {dirty && !truncated && (
                    <div className="priority-dirty-note">
                        <i className="fas fa-circle-exclamation me-2"></i>
                        Unsaved order — click <strong>Save Order</strong> to apply it.
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: 'var(--first-color)' }} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : items.length === 0 ? (
                    <div className="priority-empty">
                        <i className="fas fa-star"></i>
                        <h5>No priority genres yet</h5>
                        <p>
                            {allowEdit
                                ? 'Click "Add Genre" to promote genres into the priority list and set their order.'
                                : 'Nothing has been promoted to the priority list.'}
                        </p>
                    </div>
                ) : (
                    <div className="priority-list">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={items.map((i) => i.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {items.map((item, index) => (
                                    <SortableGenreRow
                                        key={item.id}
                                        item={item}
                                        index={index}
                                        total={items.length}
                                        rankValue={rankDrafts[item.id] !== undefined ? rankDrafts[item.id] : String(index + 1)}
                                        onRankChange={onRankChange}
                                        onRankCommit={onRankCommit}
                                        onRemove={removeFromPriority}
                                        allowEdit={allowEdit}
                                        busy={saving || busy}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </div>
                )}
            </div>

            {showPicker && (
                <Model className="common-popup add-genre-popup active">
                    <div className="edit-heading d-flex align-items-center justify-content-between">
                        <h3>Add Genres to Priority</h3>
                        <button className="common-close edit-close-btn" onClick={closePicker}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18">
                                <path fill="#ffffff"
                                    d="M19.95 16.75l-.05-.4-1.2-1-5.2-4.2c-.1-.05-.3-.2-.6-.5l-.7-.55c-.15-.1-.5-.45-1-1.1l-.1-.1c.2-.15.4-.35.6-.55l1.95-1.85 1.1-1c1-1 1.7-1.65 2.1-1.9l.5-.35c.4-.25.65-.45.75-.45.2-.15.45-.35.65-.6s.3-.5.3-.7l-.3-.65c-.55.2-1.2.65-2.05 1.35-.85.75-1.65 1.55-2.5 2.5-.8.9-1.6 1.65-2.4 2.3-.8.65-1.4.95-1.9 1-.15 0-1.5-1.05-4.1-3.2C3.1 2.6 1.45 1.2.7.55L.45.1c-.1.05-.2.15-.3.3C.05.55 0 .7 0 .85l.05.35.05.4 1.2 1 5.2 4.15c.1.05.3.2.6.5l.7.6c.15.1.5.45 1 1.1l.1.1c-.2.15-.4.35-.6.55l-1.95 1.85-1.1 1c-1 1-1.7 1.65-2.1 1.9l-.5.35c-.4.25-.65.45-.75.45-.25.15-.45.35-.65.6-.15.3-.25.55-.25.75l.3.65c.55-.2 1.2-.65 2.05-1.35.85-.75 1.65-1.55 2.5-2.5.8-.9 1.6-1.65 2.4-2.3.8-.65 1.4-.95 1.9-1 .15 0 1.5 1.05 4.1 3.2 2.6 2.15 4.3 3.55 5.05 4.2l.2.45c.1-.05.2-.15.3-.3.1-.15.15-.3.15-.45z">
                                </path>
                            </svg>
                        </button>
                    </div>
                    <div className="edit-inner">
                        <p className="priority-picker-hint">
                            New entries are appended to the end of the priority list, in the order
                            you tick them. Reorder them afterwards from the Priority tab.
                            <strong className="d-block mt-1">
                                {remainingSlots} of {MAX_PRIORITY_GENRES} slot
                                {remainingSlots === 1 ? '' : 's'} left.
                            </strong>
                        </p>

                        <div className="form-group">
                            <input
                                type="text"
                                placeholder="Search genres..."
                                value={pickerSearch}
                                onChange={(e) => setPickerSearch(e.target.value)}
                            />
                        </div>

                        <div className="priority-picker-list">
                            {candidatesLoading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border" style={{ color: 'var(--first-color)' }} role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : filteredCandidates.length === 0 ? (
                                <p className="priority-picker-empty">
                                    {candidates.length === 0
                                        ? 'Every genre is already in the priority list.'
                                        : 'No genre matches that search.'}
                                </p>
                            ) : (
                                filteredCandidates.map((genre) => {
                                    const checked = selectedIds.includes(genre.id);
                                    const blocked = !checked && selectedIds.length >= remainingSlots;
                                    return (
                                        <label
                                            className={`priority-picker-item${blocked ? ' is-disabled' : ''}`}
                                            key={genre.id}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={blocked}
                                                onChange={() => toggleSelected(genre.id)}
                                            />
                                            <span>{genre.name}</span>
                                            {checked && (
                                                <span className="priority-picker-pick">
                                                    #{items.length + selectedIds.indexOf(genre.id) + 1}
                                                </span>
                                            )}
                                        </label>
                                    );
                                })
                            )}
                        </div>

                        <div className="action-footer d-flex align-items-center justify-content-between">
                            <span className="priority-picker-count">
                                {selectedIds.length} selected · {remainingSlots - selectedIds.length} slot
                                {remainingSlots - selectedIds.length === 1 ? '' : 's'} left
                            </span>
                            <button
                                type="button"
                                className="btn"
                                onClick={addSelected}
                                disabled={adding || selectedIds.length === 0}
                            >
                                {adding ? (
                                    <div className="spinner-border text-light" style={{ width: '16px', height: '16px' }} role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                ) : 'Add to Priority'}
                            </button>
                        </div>
                    </div>
                </Model>
            )}

            <PriorityGenresStyles />
        </>
    );
};

export const PriorityGenresStyles = () => (
    <style>{`
        .priority-toolbar {
            display: flex; align-items: flex-start; justify-content: space-between;
            gap: 16px; flex-wrap: wrap; margin-bottom: 18px;
        }
        .priority-intro h5 {
            margin: 0 0 4px; font-size: 17px; color: var(--body-text-color);
            display: flex; align-items: center; gap: 10px;
        }
        .priority-slot-count {
            font-size: 12px; font-weight: 700; padding: 2px 10px; border-radius: 10px;
            color: var(--first-color); background: rgba(var(--first-color-rgb), 0.12);
        }
        .priority-slot-count.is-full {
            color: var(--price-color); background: rgba(255, 145, 57, 0.12);
        }
        .priority-intro p { margin: 0; font-size: 13px; color: var(--text-color); max-width: 620px; }
        .priority-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .priority-btn-ghost {
            background: transparent; border: 1px solid var(--border-color);
            color: var(--body-text-color); font-weight: 500; font-size: 14px;
            padding: 8px 18px; border-radius: var(--radius-sm);
        }
        .priority-btn-ghost:hover:not(:disabled) {
            border-color: var(--first-color); color: var(--first-color);
        }
        .priority-dirty-note {
            display: flex; align-items: center; font-size: 13px;
            color: var(--price-color); background: rgba(255, 145, 57, 0.1);
            border: 1px solid rgba(255, 145, 57, 0.25);
            border-radius: var(--radius-sm); padding: 8px 14px; margin-bottom: 14px;
        }
        .priority-blocked-note {
            color: var(--error-color); background: rgba(247, 64, 50, 0.1);
            border-color: rgba(247, 64, 50, 0.25);
        }
        .priority-list { display: flex; flex-direction: column; gap: 10px; max-width: 720px; }
        .priority-row {
            display: flex; align-items: center; gap: 14px;
            background: var(--glass-bg); border: 1px solid var(--border-color);
            border-radius: var(--radius-md); padding: 12px 14px;
        }
        .priority-row.is-dragging {
            border-color: var(--first-color);
            box-shadow: var(--shadow-glow); z-index: 2; position: relative;
        }
        .priority-handle {
            background: transparent; border: none; padding: 4px 2px;
            color: var(--text-color); font-size: 16px; cursor: grab; touch-action: none;
        }
        .priority-handle:active { cursor: grabbing; }
        .priority-handle:disabled { cursor: not-allowed; opacity: 0.4; }
        .priority-rank-badge {
            min-width: 42px; text-align: center; font-weight: 700; font-size: 13px;
            color: var(--first-color); background: rgba(var(--first-color-rgb), 0.12);
            border-radius: var(--radius-sm); padding: 4px 8px;
        }
        .priority-name {
            flex: 1 1 auto; min-width: 0; font-weight: 500; font-size: 15px;
            color: var(--body-text-color);
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .priority-rank-set { display: flex; align-items: center; gap: 6px; }
        .priority-rank-set label {
            margin: 0; font-size: 11px; text-transform: uppercase;
            letter-spacing: 0.05em; color: var(--text-color);
        }
        .priority-rank-set input {
            width: 62px; padding: 5px 8px; font-size: 13px; text-align: center;
            background: var(--body-bg-color); color: var(--body-text-color);
            border: 1px solid var(--border-color); border-radius: var(--radius-sm);
        }
        .priority-remove {
            background: transparent; border: 1px solid var(--border-color);
            color: var(--text-color); width: 30px; height: 30px; border-radius: 50%;
            display: flex; align-items: center; justify-content: center; font-size: 12px;
        }
        .priority-remove:hover:not(:disabled) {
            border-color: var(--error-color); color: var(--error-color);
        }
        .priority-empty {
            text-align: center; padding: 48px 20px; color: var(--text-color);
            border: 1px dashed var(--border-color); border-radius: var(--radius-md);
        }
        .priority-empty i { font-size: 32px; color: var(--first-color); opacity: 0.5; }
        .priority-empty h5 { margin: 14px 0 6px; font-size: 17px; color: var(--body-text-color); }
        .priority-empty p { margin: 0; font-size: 13px; }
        .priority-picker-hint { font-size: 12.5px; color: var(--text-color); margin-bottom: 12px; }
        .priority-picker-list {
            max-height: 320px; overflow-y: auto; margin-bottom: 16px;
            border: 1px solid var(--border-color); border-radius: var(--radius-sm); padding: 6px;
        }
        .priority-picker-item {
            display: flex; align-items: center; gap: 10px; width: 100%;
            padding: 8px 10px; border-radius: var(--radius-sm); cursor: pointer;
            font-size: 14px; color: var(--body-text-color); margin: 0;
        }
        .priority-picker-item:hover:not(.is-disabled) { background: var(--sidebar-hover); }
        .priority-picker-item.is-disabled { opacity: 0.45; cursor: not-allowed; }
        .priority-picker-item input { width: 16px; height: 16px; accent-color: var(--first-color); flex: none; }
        .priority-picker-item > span:first-of-type { flex: 1 1 auto; min-width: 0; }
        .priority-picker-pick {
            flex: none; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 10px;
            color: var(--first-color); border: 1px solid var(--first-color);
            background: rgba(var(--first-color-rgb), 0.12);
        }
        .priority-picker-empty { font-size: 13px; color: var(--text-color); text-align: center; padding: 24px 0; margin: 0; }
        .priority-picker-count { font-size: 13px; color: var(--text-color); }
        @media (max-width: 575px) {
            .priority-row { flex-wrap: wrap; gap: 10px; }
            .priority-name { flex-basis: 100%; order: 3; }
        }
    `}</style>
);

export default PriorityGenres;
